import { __awaiter, __decorate, __param, __metadata } from 'tslib';
import { Optional, ɵɵdefineInjectable, ɵɵinject, Injectable, NgModule } from '@angular/core';
import { BaseAPIService, CoreModule } from '@lbmx/phoenix-lib-core';
import { ofType, Actions } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { actions, actionTypes, selectors } from '@lbmx/app-state';
import { select, Store } from '@ngrx/store';
import { first, tap, map } from 'rxjs/operators';

class AuthConfig {
    constructor() {
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.tokenEndpoint = null;
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.refreshTokenEndpoint = null;
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.logOutEndPoint = null;
        /**
         * Url of the userinfo endpoint as defined by OpenId Connect.
         */
        this.userinfoEndpoint = null;
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.rememberMeLoadEndPoint = null;
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.getCookieRememberMeEndPoint = null;
    }
}

let AuthenticateService = class AuthenticateService {
    constructor(baseAPIService, store, actions$, config) {
        this.baseAPIService = baseAPIService;
        this.store = store;
        this.actions$ = actions$;
        this.config = config;
        this.eventsSubject = new Subject();
        this.subscriptions = [];
        this.events = this.eventsSubject.asObservable();
        if (config) {
            this.configure(config);
        }
    }
    ngOnDestroy() {
        this.subscriptions.forEach((sub) => {
            sub.unsubscribe();
        });
    }
    //#region Methods
    /*** Use this method to configure the service
     * @param config the configuration */
    configure(config) {
        this.config = config;
    }
    verify(request, endPoints, onSuccess, onError) {
        this.baseAPIService.set(endPoints.uriSecurity.verify);
        this.subscriptions.push(this.baseAPIService.post(request).subscribe(() => {
            this.store.dispatch(actions.refreshUserProfile({
                endPoints,
            }));
        }, (err) => {
            onError(err);
        }));
        this.subscriptions.push(this.actions$
            .pipe(ofType(actions.refreshUserProfileSuccess), first(), tap((action) => {
            onSuccess();
        }))
            .subscribe());
    }
    authenticate(loginRequest, endPoints, onSuccess, onError, onRequiringAuth) {
        let response;
        this.baseAPIService.set(endPoints.uriSecurity.authenticate);
        this.subscriptions.push(this.baseAPIService.post(loginRequest).subscribe((res) => {
            response = res;
            if (onRequiringAuth && response.errorCode === 206) {
                onRequiringAuth();
            }
            else {
                this.store.dispatch(actions.refreshUserProfile({
                    endPoints,
                }));
            }
        }, (err) => {
            onError(err);
        }));
        this.subscriptions.push(this.actions$
            .pipe(ofType(actions.refreshUserProfileSuccess), first(), tap((action) => {
            onSuccess(response);
        }))
            .subscribe());
    }
    updateMarketplace(marketplace, endPoints, onSuccess, onFailure) {
        this.store.dispatch(actions.updateUserProfileMarketplace({
            endPoints,
            marketplace,
        }));
        this.subscriptions.push(this.actions$
            .pipe(ofType(actions.refreshUserProfileSuccess, actions.refreshUserProfileFailure), map((action) => {
            if (action.type === actionTypes.REFRESH_USER_PROFILE_FAILURE) {
                if (onFailure) {
                    onFailure();
                }
            }
            else {
                if (onSuccess) {
                    onSuccess();
                }
            }
        }))
            .subscribe());
    }
    getLoggedIn(endPoints, onSuccess) {
        this.store.dispatch(actions.refreshUserProfile({
            endPoints,
        }));
        this.subscriptions.push(this.store
            .select(selectors.loggedIn)
            .pipe(map((loggedIn) => loggedIn))
            .subscribe((loggedIn) => {
            if (loggedIn) {
                onSuccess();
            }
        }));
    }
    getIdentityClaims() {
        let claims;
        this.subscriptions.push(this.store
            .pipe(select(selectors.userProfile), tap((userProfile) => {
            claims = userProfile;
        }))
            .subscribe());
        if (!claims) {
            return null;
        }
        return claims;
    }
    getCookieRememberMe() {
        this.baseAPIService.set(this.config.getCookieRememberMeEndPoint);
        return this.baseAPIService.get();
    }
    getCookieUserName() {
        this.baseAPIService.set(this.config.rememberMeLoadEndPoint);
        return this.baseAPIService.get();
    }
    /**
     * Returns the expiration date of the access_token
     * as milliseconds since 1970.
     */
    logOut(endPoints) {
        return __awaiter(this, void 0, void 0, function* () {
            this.store.dispatch(actions.logout({ endPoints }));
        });
    }
};
AuthenticateService.ctorParameters = () => [
    { type: BaseAPIService },
    { type: Store },
    { type: Actions },
    { type: AuthConfig, decorators: [{ type: Optional }] }
];
AuthenticateService.ɵprov = ɵɵdefineInjectable({ factory: function AuthenticateService_Factory() { return new AuthenticateService(ɵɵinject(BaseAPIService), ɵɵinject(Store), ɵɵinject(Actions), ɵɵinject(AuthConfig, 8)); }, token: AuthenticateService, providedIn: "root" });
AuthenticateService = __decorate([
    Injectable({
        providedIn: 'root',
    }),
    __param(3, Optional()),
    __metadata("design:paramtypes", [BaseAPIService,
        Store,
        Actions,
        AuthConfig])
], AuthenticateService);

//   /**
//    * Defines the logging interface the OAuthService uses
//    * internally. Is compatible with the `console` object,
//    * but you can provide your own implementation as well
//    * through dependency injection.
//    */
//   export abstract class OAuthLogger
//   {
//     abstract debug(message?: any, ...optionalParams: any[]): void;
//     abstract info(message?: any, ...optionalParams: any[]): void;
//     abstract log(message?: any, ...optionalParams: any[]): void;
//     abstract warn(message?: any, ...optionalParams: any[]): void;
//     abstract error(message?: any, ...optionalParams: any[]): void;
//   }
/**
 * Defines a simple storage that can be used for
 * storing the tokens at client side.
 * Is compatible to localStorage and sessionStorage,
 * but you can also create your own implementations.
 */
class OAuthStorage {
}

class DemoStorage {
    getItem(key) {
        if (typeof Storage !== 'undefined' && key != null) {
            return sessionStorage.getItem(key);
        }
        return null;
    }
    removeItem(key) {
        if (typeof Storage !== 'undefined' && key != null) {
            sessionStorage.removeItem(key);
        }
    }
    setItem(key, data) {
        if (typeof Storage !== 'undefined' && key != null) {
            sessionStorage.setItem(key, data);
        }
    }
}
// export function createDefaultStorage()
// {
//   //alert('createDefaultStorage');
//   return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
// }
let AuthModule = class AuthModule {
};
AuthModule = __decorate([
    NgModule({
        declarations: [],
        providers: [
            // {
            //   provide: OAuthStorage,
            //   useFactory: createDefaultStorage
            // },
            // { provide: OAuthStorage, useValue: localStorage },
            {
                provide: OAuthStorage,
                useClass: DemoStorage,
            },
            AuthenticateService,
        ],
        imports: [CoreModule],
        exports: [],
    })
], AuthModule);

class UserSession {
}

class UserAuthRequest {
}

class VerifyAuthRequest {
}

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthConfig, AuthModule, AuthenticateService, DemoStorage, UserAuthRequest, UserSession, VerifyAuthRequest, OAuthStorage as ɵa };
//# sourceMappingURL=lbmx-phoenix-lib-auth.js.map
